import { CheckOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Space, Table, Tag, message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTenantDateTime } from '../lib/tenant';

type Notification = {
  id: string;
  eventType: string;
  titleKey: string;
  bodyKey: string;
  payload: Record<string, unknown>;
  readAt: string | null;
  version: number;
  createdAt: string;
};
export function NotificationsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, holder] = message.useMessage();
  const load = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/notifications', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error();
      setItems(((await response.json()) as { items: Notification[] }).items);
    } catch {
      await messageApi.error(t('notifications.loadFailed'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const read = async (row: Notification) => {
    const response = await fetch(`/api/v1/notifications/${row.id}/read`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ version: row.version }),
    });
    if (!response.ok) {
      await messageApi.error(t('notifications.readFailed'));
      return;
    }
    window.dispatchEvent(new Event('notifications:changed'));
    await load();
  };
  return (
    <section>
      {holder}
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => void load()}
        >
          {t('notifications.refresh')}
        </Button>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 50 }}
        columns={[
          { title: t('notifications.event'), dataIndex: 'eventType' },
          {
            title: t('notifications.status'),
            key: 'status',
            render: (_, row: Notification) => (
              <Tag color={row.readAt ? 'default' : 'blue'}>
                {row.readAt
                  ? t('notifications.read')
                  : t('notifications.unread')}
              </Tag>
            ),
          },
          {
            title: t('notifications.createdAt'),
            dataIndex: 'createdAt',
            render: (value: string) => formatTenantDateTime(value),
          },
          {
            title: t('notifications.actions'),
            key: 'actions',
            render: (_, row: Notification) =>
              !row.readAt && (
                <Button icon={<CheckOutlined />} onClick={() => void read(row)}>
                  {t('notifications.markRead')}
                </Button>
              ),
          },
        ]}
      />
    </section>
  );
}
